import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Select, Tooltip } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import { selectDarkMode } from '../../../userBrowserSettings/store/browserSettings.selectors';

import DropdownArrowIcons from '../../../components/common/dropdown/DropdownArrowIcons';
import AIOptimizerAdditionalInfoModal from './AIOptimizerAdditionalInfoModal';
import AiOptimizerModalHeader from './AiOptimizerModalHeader';
import CloseIcon from '../../../resources/icons/CloseIcon';

import {
    aiInstanceTypes,
    aiOptimizerContentTypes,
    aiOptimizerOptions,
    aiOutlineOptions,
} from '../../../constants/aiOptimizerConstants';

import { getAdditionalInfoFieldValidator } from '../../../utils/helpers/getBriefExtrasFormItems';
import { getThemeColorClass, getTooltipOverlayClass } from '../../../utils/helpers/themeHelpers';
import { useOpen } from '../../../utils/hooks/useOpen';

import './AiOptimizerOptionModal.scss';

const classes = {
    aiOptimizerOptionsWrapper: 'ai-optimizer-options-wrapper',
    aiOptimizerOptionList: 'ai-optimizer-option-list',
    aiOptimizerOptionBtn: 'ai-optimizer-option-btn',
    creditsLabel: 'credits-label',
};

const AiOptimizerOptionModal = ({
    isOpen,
    handleOpen,
    onSubmit = () => null,
    resetFormData = false,
    keywordData = {},
    initialValues = {},
    showOptions = false,
    typeSelected = '',
}) => {
    const darkMode = useSelector(selectDarkMode);
    const { isOpen: isInfoOpen, handleOpen: handleInfoOpen } = useOpen();
    const { isOpen: isAiOptionDropdownOpen, handleOpen: handleAiOptionDropdownOpen } = useOpen();
    const { isOpen: isContentTypeDropdownOpen, handleOpen: handleContentTypeDropdownOpen } =
        useOpen();

    const [form] = Form.useForm();

    const showAdditionalInfo = useMemo(() => {
        if (!typeSelected?.length > 0) return true;

        return ![aiInstanceTypes.BRIEF, aiInstanceTypes.CONTENT].includes(typeSelected);
    }, [typeSelected]);

    const buttonAdditionalLabel = useMemo(() => {
        let labelText = '';

        switch (typeSelected) {
            case aiInstanceTypes.BRIEF:
                labelText = aiOutlineOptions.newBrief.credits;
                break;
            case aiInstanceTypes.OUTLINE:
                labelText = aiOutlineOptions.newOutline.credits;
                break;
            case aiInstanceTypes.CONTENT:
                labelText = aiOutlineOptions.generateContent.credits;
                break;
            default:
                labelText = '';
        }

        return labelText?.length > 0 ? <small>&nbsp;{labelText}</small> : null;
    }, [typeSelected]);

    const optionsModalTitle = useMemo(() => {
        return typeSelected.length > 0 ? `AI ${typeSelected} Options` : undefined;
    }, [typeSelected]);

    const resetOptionModalForm = (formInstance = null) => {
        formInstance?.resetFields([
            'ai_content_type',
            'ai_optimizer_options',
            'ai_additional_info',
            'ai_include_faqs',
        ]);
    };

    useEffect(() => {
        if (resetFormData) {
            resetOptionModalForm(form);
        }
    }, [resetFormData, form]);

    useEffect(() => {
        if (!isOpen) {
            resetOptionModalForm(form);
        }
    }, [isOpen, form]);

    return (
        <Modal
            centered
            forceRender
            title={<AiOptimizerModalHeader {...keywordData} title={optionsModalTitle} />}
            open={isOpen}
            onCancel={handleOpen}
            footer={null}
            closeIcon={<CloseIcon />}
            wrapClassName={`${classes.aiOptimizerOptionsWrapper} ${getThemeColorClass(darkMode)}`}
        >
            <Form
                form={form}
                layout='vertical'
                onFinish={onSubmit}
                initialValues={{
                    is_ai_generated: true,
                    ai_content_type: null,
                    ai_optimizer_options: null,
                    ai_additional_info: '',
                    ai_include_faqs: false,
                    ...initialValues,
                }}
            >
                <div>
                    <Form.Item hidden name='is_ai_generated'>
                        <Input type='hidden' value={true} />
                    </Form.Item>
                    <Form.Item hidden name='ai_include_faqs'>
                        <Input type='hidden' value={false} />
                    </Form.Item>
                    {showOptions ? (
                        <Form.Item
                            name='ai_optimizer_options'
                            label='What would you like to generate with AI?'
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select an option',
                                },
                            ]}
                        >
                            <Select
                                placeholder='Select option'
                                popupClassName={classes.aiOptimizerOptionList}
                                onChange={() => null}
                                options={aiOptimizerOptions.map((option) => ({
                                    value: option.value,
                                    label: (
                                        <>
                                            <span>{option.label}</span>
                                            <span className={classes.creditsLabel}>
                                                {option.credits}
                                            </span>
                                        </>
                                    ),
                                }))}
                                open={isAiOptionDropdownOpen}
                                onDropdownVisibleChange={handleAiOptionDropdownOpen}
                                suffixIcon={
                                    <DropdownArrowIcons isDropdownOpen={isAiOptionDropdownOpen} />
                                }
                                allowClear
                            ></Select>
                        </Form.Item>
                    ) : (
                        <Form.Item hidden name='ai_optimizer_options'>
                            <Input type='hidden' value={null} />
                        </Form.Item>
                    )}
                    <Form.Item
                        name='ai_content_type'
                        label='Choose the type of content you want to generate'
                        rules={[
                            {
                                required: true,
                                message: 'Please select a content type',
                            },
                        ]}
                    >
                        <Select
                            placeholder='Select content type'
                            popupClassName={classes.aiOptimizerOptionList}
                            onChange={() => null}
                            options={aiOptimizerContentTypes}
                            open={isContentTypeDropdownOpen}
                            onDropdownVisibleChange={handleContentTypeDropdownOpen}
                            suffixIcon={
                                <DropdownArrowIcons isDropdownOpen={isContentTypeDropdownOpen} />
                            }
                            allowClear
                        ></Select>
                    </Form.Item>
                    {showAdditionalInfo ? (
                        <Form.Item
                            name='ai_additional_info'
                            label={
                                <>
                                    Provide additional information (optional)
                                    <Tooltip
                                        overlayClassName={getTooltipOverlayClass(darkMode)}
                                        title='Customize your content with specific details such as company details (name, about, contact info), target location, product or brand names you’d like to promote, or anything else you’d like to include.'
                                    >
                                        <InfoCircleOutlined />
                                    </Tooltip>
                                    {/* <InfoCircleOutlined onClick={() => handleInfoOpen(true)} /> */}
                                </>
                            }
                            rules={[
                                {
                                    required: false,
                                    message: 'An additional information should be a string',
                                },
                                () => getAdditionalInfoFieldValidator(),
                            ]}
                        >
                            <TextArea placeholder='' onChange={() => null} />
                        </Form.Item>
                    ) : (
                        <Form.Item hidden name='ai_additional_info'>
                            <Input type='hidden' value={null} />
                        </Form.Item>
                    )}
                    <Form.Item className={classes.aiOptimizerOptionBtn}>
                        <Button type='primary' htmlType='submit' size='large'>
                            Generate
                            {buttonAdditionalLabel}
                        </Button>
                    </Form.Item>
                </div>
            </Form>
            <AIOptimizerAdditionalInfoModal isOpen={isInfoOpen} handleOpen={handleInfoOpen} />
        </Modal>
    );
};

export default AiOptimizerOptionModal;
